<template>
	<div>
		<div v-if="showSpecialContextMessage">
			You can use your feature by opening any {{ appName }} {{ elementPlacementName }} in
			<b>{{ contextsLocationsExplanation.toLowerCase() }}</b> and clicking on the button that looks like this
		</div>
		<div v-else-if="contextName">
			You can use your feature by opening {{ elementPlacementName }}<br />in {{ appName }} and clicking on the button that looks like this
		</div>
		<div class="width-100 flex-centerize">
			<div v-if="appName" class="mt-9 icon-container" :style="{ backgroundColor: backgroundColor }">
				<ElementTreeBlockButton
					:element="element"
					:isNative="isNativeElement"
					:placement="elementPlacement"
					:workflow="installedWorkflow"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { getNode } from '@/store/modules/nodes/getters';
import { getService, getServiceBySlug } from '@/store/modules/apps/getters';
import { getServiceDataBySlug } from '@/store/modules/apps/actions';
import { getInstallable } from '@/store/modules/installables/getters';
import { element, getWorkflow, isNativeElement } from '@/store/modules/workflows/getters';
import { getConfigurationResponse } from '@/store/modules/workflowConfigurations/getters';
import { getInstallable as getInstallableAction } from '@/store/modules/installables/actions';
import { getWorkflowData } from '@/store/modules/workflows/actions';

import PlacementService from '@/utils/placement-service';
import ExtensionCommunicationService from '@/utils/extension-communication-service';

import ElementTreeBlockButton from '@/components/FlowTree/FlowTreeBlock/ElementTreeBlockButton/ElementTreeBlockButton';
import { mapGettersShorthand } from '@/store/utils/mappers';
import { getPlacement } from '@/store/modules/placements/getters';
import { getContext } from '@/store/modules/contexts/getters';

export default {
	name: 'AddOnLocationExplanation',
	props: {
		wfID: { type: Number },
	},
	components: {
		ElementTreeBlockButton,
	},
	computed: {
		...mapGetters([
			isNativeElement.fullPath,
			getServiceBySlug.fullPath,
			getService.fullPath,
			getNode.fullPath,
			getConfigurationResponse.fullPath,
			getInstallable.fullPath,
			getWorkflow.fullPath,
		]),
		...mapGettersShorthand({ getPlacement, getContext, getElement: element }),
		elementPlacement() {
			return this.getPlacement(this.element?.placement);
		},
		elementContext() {
			return this.getContext(this.element?.context);
		},
		elementPlacementName() {
			return this.elementPlacement ? PlacementService.getFullItemName(this.elementPlacement) : null;
		},
		service() {
			return (
				this[getServiceBySlug.fullPath](this.installable?.feature_context_service?.service_slug) ||
				this[getService.fullPath](this.elementPlacement?.service)
			);
		},
		appName() {
			return this.service?.service_name;
		},
		contextName() {
			return this.elementContext?.title || this.elementPlacementName;
		},
		element() {
			return this.getElement(this.installedWorkflow.id);
		},
		actionContextNode() {
			return this[getNode.fullPath](this.element?.action_context_node);
		},
		mappingSourceFieldUuid() {
			return this.actionContextNode?.user_schema_mappings_source_field_uuid;
		},
		conditionalRenderingFields() {
			return this.elementContext?.conditional_rendering_fields;
		},
		elementIconColor() {
			return this.element?.metadata?.injectionStyle.icon.fillColor.replace('#', '');
		},
		backgroundColor() {
			return this.pickTextColorBasedOnBgColorSimple(this.elementIconColor, '#FFFFFF', '#000000');
		},
		isNativeElement() {
			return this[isNativeElement.fullPath](this.installedWorkflow.id);
		},
		showInstallChrome() {
			return this.appName && !this.isExtensionInstalled;
		},
		fieldResponses() {
			const generalConfigResponse = this[getConfigurationResponse.fullPath](this.installedWorkflow.current_version_general_setup) || {};
			const personalConfigResponse = this[getConfigurationResponse.fullPath](this.installedWorkflow.current_version_user_setup) || {};
			return { ...generalConfigResponse, ...personalConfigResponse };
		},
		mappingSourceResponse() {
			return this.fieldResponses[this.mappingSourceFieldUuid];
		},
		mappingSourceParamsMapLabels() {
			return this.mappingSourceResponse?.params_map_labels || {};
		},
		contextsLocationsExplanation() {
			return this.conditionalRenderingFields?.map((field) => this.mappingSourceParamsMapLabels[field]).join(', ');
		},
		showSpecialContextMessage() {
			return this.contextName && this.conditionalRenderingFields?.length > 0;
		},

		installableId() {
			return this.installedWorkflow.installed_from;
		},
		installedWorkflow() {
			return this[getWorkflow.fullPath](this.wfID);
		},
		installable() {
			return this[getInstallable.fullPath](this.installableId);
		},
	},
	methods: {
		...mapActions([getWorkflowData.fullPath, getInstallableAction.fullPath, getServiceDataBySlug.fullPath]),
		pickTextColorBasedOnBgColorSimple(bgColor = '#fff', lightColor, darkColor) {
			var color = bgColor.charAt(0) === '#' ? bgColor.substring(1, 7) : bgColor;
			var r = parseInt(color.substring(0, 2), 16); // hexToR
			var g = parseInt(color.substring(2, 4), 16); // hexToG
			var b = parseInt(color.substring(4, 6), 16); // hexToB
			return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? darkColor : lightColor;
		},
		goToChromeStore() {
			const { VUE_APP_CHROME_EXTENSION_APP_URL: chromeAppURL } = process.env;
			window.open(chromeAppURL, '_blank');
			this.navigateTo({ name: 'PinExtension' });
		},
		getInstalledWorkflow() {
			return this[getWorkflowData.fullPath]({ id: this.wfID, needFetchNodes: true });
		},
		async getInstallableAction() {
			if (this.installableId) {
				return (await this[getInstallableAction.fullPath]({ id: this.installableId })) || {};
			} else {
				return {};
			}
		},
	},
	async created() {
		await this.getInstalledWorkflow();
		await this.getInstallableAction();
		if (this.installable?.feature_context_service?.service_slug) {
			await this[getServiceDataBySlug.fullPath]({ slug: this.installable.feature_context_service.service_slug });
		}
		if (this.installedWorkflow) {
			ExtensionCommunicationService.announceWorkflow(this.installedWorkflow);
		}
		this.isExtensionInstalled = await ExtensionCommunicationService.isInstalled();
	},
	data() {
		return {
			isExtensionInstalled: false,
		};
	},
};
</script>

<style scoped lang="scss">
.title-text {
	font-size: 20px;
	font-weight: bold;
	text-align: center;
	color: var(--primary-font-color);
}
.icon-container {
	height: 42px;
	padding: 0px 20px;
	border-radius: 6px;
	background: var(--bg-color);
	border: 1px solid var(--border-color);
}
.continue-button {
	padding: 0px;
	width: 218px;
	height: 40px;
	color: var(--bg-color);
	font-size: 13px !important;
	background: var(--bold-border-color) !important;
}
.install-button {
	background: var(--bg-color) !important;
	border: 1px solid var(--bold-border-color);
	color: var(--bold-border-color);
	font-size: 13px !important;
	padding: 0px !important;
	width: 218px;
}
.install-text {
	color: var(--primary-font-color);
	font-weight: bold;
	font-size: 18px;
}
</style>
